import type { OrderItem } from '../types';
import type { EvaluateCartItem } from '@/services/promotion.service';

/**
 * Dựng danh sách dòng hàng gửi lên `/pos/promotions/evaluate`.
 *
 * Dòng quà (`is_gift`) BỊ LOẠI, vì hai lý do độc lập:
 *   1. Quà giá 0đ vẫn đếm vào `ConditionType.CART_MIN_ITEMS` — một CTKM
 *      "mua tối thiểu 3 món" sẽ tự thoả nhờ chính món quà nó vừa tặng.
 *   2. Thêm quà làm giỏ đổi ⇒ evaluate chạy lại ⇒ có thể sinh quà tiếp.
 */
export function buildEvaluateItems(items: OrderItem[]): EvaluateCartItem[] {
  return items
    .filter((i) => !i.is_gift)
    .map((i) => ({
      product_id: i.productId,
      // TCR-1369: CTKM có thể chỉ đích danh MỘT biến thể (màn cấu hình cho
      // chọn biến thể). Không gửi id biến thể thì engine chỉ khớp được tới cấp
      // sản phẩm — "mua biến thể A tặng quà" áp cho cả biến thể B. Hai đường
      // ghi đơn của BE (`pos-order-payment`, `orders-aggregate`) cũng gửi field
      // này, nên preview ở đây và đơn thật chấm cùng một luật.
      variant_id: i.variantId,
      quantity: i.quantity,
      // TCR-1150 Bug2: BE lúc tạo đơn thật (`pos-order-payment.service.ts`)
      // gửi giá SAU chiết khấu dòng (net) cho engine khuyến mãi — gửi giá
      // GỐC ở đây khiến preview lệch với số thực tế lưu vào đơn (vd 15% ×
      // giá gốc = 45.000 lúc preview, nhưng 15% × giá net mới là số được
      // lưu = 40.500). Tính net y hệt công thức BE (`unitPrice -
      // unitPrice*discount_percent/100`) để preview khớp số thật sẽ lưu.
      unit_price: i.price - (i.price * i.discountPercent) / 100,
      // TCR-1143: dòng mua theo giá flash sale — BE (`evaluate()`) đọc field
      // này để áp giá/khuyến mãi flash sale đúng dòng thay vì giá gốc.
      // Hai nguồn cùng mang id của `FlashSaleItemV2`: `flash_sale_item_id` là
      // dòng dựng qua `variantToOrderItem` (giá flash áp ngay lúc thêm hàng),
      // `flash_sale_id` là đường cũ do panel flash sale gắn (TCR-1143, tên
      // field gây hiểu nhầm nhưng giá trị vẫn là id ITEM). Đọc thiếu một
      // trong hai là engine mất dấu chương trình và tính lại giảm giá trên
      // giá ĐÃ giảm.
      flash_sale_item_id: i.flash_sale_item_id ?? i.flash_sale_id,
      // Fix round 2 (F6): CHỈ gửi danh mục ĐẦU TIÊN dù `categoryIds` có nhiều
      // phần tử. Lúc GHI ĐƠN THẬT, BE chỉ lưu MỘT danh mục duy nhất/dòng —
      // `product_category_id = product.category_id[0]`
      // (`tcr-erp-be-tenant/src/modules/pos/providers/
      // pos-order-pricing.service.ts:872-875`). Nếu preview ở đây gửi CẢ
      // MẢNG, một CTKM/voucher giới hạn theo danh mục có thể khớp ở FE (vì
      // danh mục thứ 2/3 của sản phẩm nằm trong điều kiện) nhưng BE lúc chốt
      // đơn chỉ thấy `category_id[0]` — không khớp. Kết quả: POS báo "hợp lệ",
      // thu ngân đã thu tiền theo số đó, rồi BE từ chối/tính khác lúc ghi đơn
      // — lệch tiền thật. ĐỪNG "sửa lại cho đúng" thành gửi cả mảng — điều đó
      // ngược lại với thứ BE thực sự honor.
      ...(i.categoryIds?.[0] ? { category_ids: [i.categoryIds[0]] } : {}),
    }));
}
